import { Box, HStack, VStack } from '@chakra-ui/react';
import { Button } from 'Atoms';
import { TaskFilterOptions } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/MetricsTable/MetricsTable.utils';
import { TaskList } from 'containers/TaskList/components/TaskList';
import {
  TaskListFilterBar,
  TaskListProgressBar,
} from 'containers/TaskList/components/TaskListHeader';
import { SelectedAssessmentOption } from 'containers/TaskList/components/TaskManagement';
import {
  TaskListSortBy,
  useGetCompanyOptions,
  useTaskListData,
} from 'containers/TaskList/hooks/TaskList.hooks';
import { Loader } from 'Molecules';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from 'Tokens';
import { ListIcon } from 'Tokens/Icons/Data';
import { useTranslation } from 'utils/translation';

export const HomeTaskBoard = () => {
  const { t } = useTranslation('home');
  const [selectedAssessment, setSelectedAssessment] = useState<SelectedAssessmentOption>();
  const [sortBy, setSortBy] = useState<TaskListSortBy>('deadline');
  const { companyOptions, loading: companyOptionsLoading } = useGetCompanyOptions();

  const navigate = useNavigate();

  const {
    taskList: filteredTasks,
    userId,
    loading,
    error,
  } = useTaskListData(
    {
      selectedAssessment,
      taskFilterOption: TaskFilterOptions.yourTasks,
      sortBy,
      sortDirection: 'asc',
    },
    true
  );

  // Progress counts
  const completed = filteredTasks
    .filter((row) => row.task)
    .reduce((acc, row) => acc + (row.task?.completedCount ?? 0), 0);

  const inProgress = filteredTasks
    .filter((row) => row.task)
    .reduce((acc, row) => acc + (row.task?.inProgressCount ?? 0), 0);

  const notStarted = filteredTasks
    .filter((row) => row.task)
    .reduce((acc, row) => acc + (row.task?.notStartedCount ?? 0), 0);

  const total = completed + inProgress + notStarted;

  return (
    <VStack alignItems="stretch" justifyContent="flex-start" spacing="16px" mb="48px">
      <HStack>
        <Typography variant="bodyStrong" color="text.default">
          {t('home:cards.taskBoard')}
        </Typography>
      </HStack>
      <VStack
        spacing="24px"
        alignItems="stretch"
        pb="48px"
        p="20px"
        border="1px solid"
        borderColor="border.decorative"
        borderRadius="8px"
      >
        <TaskListFilterBar
          selectedAssessment={selectedAssessment}
          setSelectedAssessment={setSelectedAssessment}
          sortBy={sortBy}
          setSortBy={setSortBy}
          companyOptions={companyOptions}
          isHomePage
        />
        {loading ? (
          <Box h="100px">
            <Loader isLoaded={!loading} />
          </Box>
        ) : (
          <VStack alignItems="stretch" gap="24px">
            {!!filteredTasks.length && (
              <TaskListProgressBar
                completed={completed}
                inProgress={inProgress}
                notStarted={notStarted}
                total={total}
              />
            )}
            <VStack alignItems="start" gap="12px">
              <TaskList
                taskList={filteredTasks}
                selectedAssessment={selectedAssessment}
                isHomepage
              />
              <Button
                variant="ghost"
                leftIcon={<ListIcon />}
                onClick={() => navigate(`task-management?taskUserId=${userId}`, { replace: true })}
                alignVertically
              >
                View all tasks
              </Button>
            </VStack>
          </VStack>
        )}
      </VStack>
    </VStack>
  );
};
